import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Heading,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  useToast
} from '@chakra-ui/react';
import FileTable from './DeriveFile/FileTable';
import { getServiceLogs, runServiceDashboard } from '../services/ServiceDashboard';
import { showErrorToast, showSuccessToast } from '../util/errorHandler';


const ServiceDashboard = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef();
  const toast = useToast();

  const columns = [
    { key: 'display', label: 'Service Name' },
    { key: 'count', label: 'Count' },
    { key: 'ttl', label: 'TTL' },
    { key: 'lastUpdated', label: 'Last Updated' },
    { key: 'action', label: 'Action' }
  ];

  const fetchServices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getServiceLogs();
      setServices(response?.data?.data || []);
    } catch (error) {
      console.error('Error fetching services:', error);
            showErrorToast(toast, "Failed to fetch services data");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleRunService = (service) => {
    // Only allow running service if it's enabled (count >= 1)
    if (service?.enabled) {
      setSelectedService(service);
      onOpen();
    }
  };

  const handleConfirmRun = async () => {
    try {
      // Call appropriate API based on the service key
      if (selectedService?.key === 'updateCount') {
        await runServiceDashboard(selectedService?.key,selectedService?.id);
      } else if (selectedService?.key === 'extendedTags') {
        await runServiceDashboard(selectedService?.key,selectedService?.id);
      } else if (selectedService?.key ==='generateCaption') {
        await runServiceDashboard(selectedService?.key,selectedService?.id);
      } else {
        throw new Error('Unknown service type');
      }
      
            showSuccessToast(toast, "Service executed successfully");
      onClose();
      // Refresh the services data
      fetchServices();
    } catch (error) {
      console.error('Error running service:', error);
            showErrorToast(toast, "Failed to execute service");
    }
  };

  const handleCancel = () => {
    setSelectedService(null);
    onClose();
  };

  // Transform services data for the table
  const tableData = services?.length > 0 ? services?.map((service, index) => ({
    id: service.key || index,
    display: service.display || 'Unknown Service',
    count: service?.count || 0,
    ttl: service.ttl || 0,
    lastUpdated: service?.lastUpdated || 'Unknown',
    key: service?.key,
    action: 'run',
    enabled: (service?.count || 0) >= 1
  })) : [];

  return (
    <Box p={6}>
      <Heading mb={6} size="lg">Service Dashboard</Heading>
      
      <FileTable
        data={tableData}
        columns={columns}
        loading={loading}
        refreshKey={0}
        fetchFiles={() => {}} // Not needed for this use case
        currentPage={1}
        searchText=""
        onRefresh={fetchServices}
        handleCommit={() => {}} // Not needed
        handleAlgoliaTrigger={() => {}} // Not needed
        customActionHandler={handleRunService}
      />

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Service Execution Warning
            </AlertDialogHeader>
            <AlertDialogBody>
              Running this service will slow down the performance of the website for at least 6 hours.
              Would you like to continue?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={handleCancel}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleConfirmRun} ml={3}>
                Continue
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default ServiceDashboard;
